<template>
  <div class="ai-insights-panel">
    <!-- Header -->
    <div class="insights-header">
      <div class="header-content">
        <h3>
          <i class="bi bi-lightbulb-fill"></i>
          AI Insights & Suggestions
        </h3>
        <p>Automated analysis and intelligent recommendations</p>
      </div>
      <div class="insights-toggle">
        <button @click="togglePanel" class="toggle-btn" :class="{ active: isExpanded }">
          <i class="bi" :class="isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
        </button>
      </div>
    </div>

    <!-- Main Content -->
    <div v-if="isExpanded" class="insights-content">
      <!-- Duplicate Detection Alert -->
      <div v-if="duplicateAlerts.length > 0" class="insight-section duplicate-alerts">
        <div class="section-header">
          <h4>
            <i class="bi bi-exclamation-triangle-fill"></i>
            Potential Duplicates Detected
          </h4>
        </div>
        <div class="duplicate-list">
          <div v-for="duplicate in duplicateAlerts" :key="duplicate.player.id" class="duplicate-item"
            :class="getConfidenceClass(duplicate.confidenceLevel)">
            <div class="duplicate-info">
              <div class="player-match">
                <strong>{{ duplicate.player.name }}</strong>
                <span class="similarity">{{ Math.round(duplicate.similarity * 100) }}% match</span>
              </div>
              <div class="match-details">
                {{ duplicate.matchDetails.fixture }} -
                {{ formatDate(duplicate.matchDetails.date) }}
              </div>
              <div class="confidence-badge">
                {{ duplicate.confidenceLevel }} Confidence
              </div>
            </div>
            <div class="duplicate-actions">
              <button @click="reviewDuplicate(duplicate)" class="btn-review">
                Review
              </button>
              <button @click="dismissDuplicate(duplicate)" class="btn-dismiss">
                Dismiss
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Player Categorization -->
      <div v-if="playerCategory" class="insight-section player-category">
        <div class="section-header">
          <h4>
            <i class="bi bi-person-badge-fill"></i>
            Player Analysis
          </h4>
        </div>
        <div class="category-content">
          <div class="primary-category">
            <div class="category-badge" :class="getCategoryClass(playerCategory.primaryType)">
              {{ playerCategory.primaryType }}
            </div>
            <div class="confidence-meter">
              <div class="confidence-bar">
                <div class="confidence-fill" :style="{ width: playerCategory.confidence * 100 + '%' }"></div>
              </div>
              <span class="confidence-text">{{ Math.round(playerCategory.confidence * 100) }}%
                confidence</span>
            </div>
          </div>

          <div class="category-description">
            {{ playerCategory.description }}
          </div>

          <div v-if="
            playerCategory &&
            Array.isArray(playerCategory.strengths) &&
            playerCategory.strengths.length > 0
          " class="strengths-weaknesses">
            <div class="strengths">
              <h5>Strengths</h5>
              <div class="attribute-tags">
                <span v-for="strength in playerCategory.strengths" :key="strength" class="attribute-tag strength">
                  {{ strength }}
                </span>
              </div>
            </div>

            <div v-if="playerCategory.weaknesses.length > 0" class="weaknesses">
              <h5>Areas for Development</h5>
              <div class="attribute-tags">
                <span v-for="weakness in playerCategory.weaknesses" :key="weakness" class="attribute-tag weakness">
                  {{ weakness }}
                </span>
              </div>
            </div>
          </div>

          <div v-if="playerCategory.potentialRole" class="potential-role">
            <h5>Suggested Role</h5>
            <span class="role-badge">{{ playerCategory.potentialRole }}</span>
          </div>
        </div>
      </div>

      <!-- Follow-up Actions -->
      <div v-if="followUpActions.length > 0" class="insight-section follow-up-actions">
        <div class="section-header">
          <h4>
            <i class="bi bi-list-check"></i>
            Recommended Actions
          </h4>
        </div>
        <div class="actions-list">
          <div v-for="action in followUpActions" :key="action.action" class="action-item"
            :class="getPriorityClass(action.priority)">
            <div class="action-icon">
              <i class="bi" :class="getActionIcon(action.icon)"></i>
            </div>
            <div class="action-content">
              <div class="action-header">
                <h5>{{ action.action }}</h5>
                <span class="priority-badge" :class="getPriorityClass(action.priority)">
                  {{ action.priority }} Priority
                </span>
              </div>
              <p class="action-description">{{ action.description }}</p>
              <div class="action-meta">
                <span class="timeline">
                  <i class="bi bi-clock"></i>
                  {{ action.timeline }}
                </span>
                <span class="responsible">
                  <i class="bi bi-person"></i>
                  {{ action.responsible }}
                </span>
              </div>
            </div>
            <div class="action-controls">
              <button @click="completeAction(action)" class="btn-complete">
                <i class="bi bi-check"></i>
              </button>
              <button @click="scheduleAction(action)" class="btn-schedule">
                <i class="bi bi-calendar-plus"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Scout Workload Analysis (if applicable) -->
      <div v-if="scoutWorkload && showScoutAnalysis" class="insight-section scout-workload">
        <div class="section-header">
          <h4>
            <i class="bi bi-person-workspace"></i>
            Scout Performance Analysis
          </h4>
        </div>
        <div class="workload-content">
          <div class="workload-metrics">
            <div class="metric-item">
              <span class="metric-value">{{
                scoutWorkload.gamesAttended
                }}</span>
              <span class="metric-label">Games Attended</span>
            </div>
            <div class="metric-item">
              <span class="metric-value">{{
                scoutWorkload.efficiency.toFixed(1)
                }}</span>
              <span class="metric-label">Leads per Game</span>
            </div>
            <div class="metric-item">
              <span class="metric-value">{{
                scoutWorkload.averageRating.toFixed(1)
                }}</span>
              <span class="metric-label">Avg Rating Given</span>
            </div>
          </div>

          <div v-if="scoutWorkload.recommendations.length > 0" class="workload-recommendations">
            <h5>Recommendations</h5>
            <ul>
              <li v-for="rec in scoutWorkload.recommendations" :key="rec">
                {{ rec }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Settings -->
      <div class="insights-settings">
        <button @click="toggleSettings" class="settings-toggle">
          <i class="bi bi-gear"></i>
          AI Settings
        </button>

        <div v-if="showSettings" class="settings-panel">
          <div class="setting-item">
            <label class="setting-label">
              <input type="checkbox" v-model="settings.enableDuplicateDetection" @change="updateSettings" />
              Enable duplicate player detection
            </label>
          </div>
          <div class="setting-item">
            <label class="setting-label">
              <input type="checkbox" v-model="settings.enableAutoSuggestions" @change="updateSettings" />
              Show automated follow-up suggestions
            </label>
          </div>
          <div class="setting-item">
            <label class="setting-label">
              <input type="range" min="0.5" max="0.9" step="0.1" v-model="settings.duplicateThreshold"
                @change="updateSettings" />
              Duplicate detection sensitivity:
              {{ Math.round(settings.duplicateThreshold * 100) }}%
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AIInsightsService from "@/services/aiInsights-service.js";

export default {
  name: "AIInsightsPanel",
  props: {
    currentPlayer: {
      type: Object,
      default: () => ({}),
    },
    gameContext: {
      type: Object,
      default: () => ({}),
    },
    allGameReports: {
      type: Array,
      default: () => [],
    },
    scoutId: {
      type: [String, Number],
      default: null,
    },
    showScoutAnalysis: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["action-selected", "duplicate-reviewed", "settings-changed"],
  data() {
    return {
      isExpanded: true,
      showSettings: false,
      duplicateAlerts: [],
      playerCategory: null,
      followUpActions: [],
      scoutWorkload: null,
      settings: {
        enableDuplicateDetection: true,
        enableAutoSuggestions: true,
        duplicateThreshold: 0.7,
      },
    };
  },
  watch: {
    currentPlayer: {
      handler(newPlayer) {
        if (newPlayer && Object.keys(newPlayer).length > 0) {
          this.analyzeCurrentPlayer();
        }
      },
      deep: true,
      immediate: true,
    },
    allGameReports: {
      handler() {
        if (this.currentPlayer && Object.keys(this.currentPlayer).length > 0) {
          this.analyzeCurrentPlayer();
        }
      },
      immediate: true,
    },
    scoutId: {
      handler(newScoutId) {
        if (newScoutId && this.showScoutAnalysis) {
          this.analyzeScoutWorkload();
        }
      },
      immediate: true,
    },
  },
  methods: {
    analyzeCurrentPlayer() {
      if (!this.currentPlayer || !this.currentPlayer.name) return;

      // Duplicate Detection
      if (this.settings.enableDuplicateDetection) {
        this.duplicateAlerts = AIInsightsService.detectDuplicatePlayers(
          this.allGameReports,
          this.currentPlayer,
        ).filter((dup) => dup.similarity >= this.settings.duplicateThreshold);
      }

      // Player Categorization
      this.playerCategory = AIInsightsService.categorizePlayer(
        this.currentPlayer,
      );

      // Follow-up Actions
      if (this.settings.enableAutoSuggestions) {
        this.followUpActions = AIInsightsService.generateFollowUpActions(
          this.currentPlayer,
          this.gameContext,
        );
      }
    },

    analyzeScoutWorkload() {
      if (this.scoutId) {
        this.scoutWorkload = AIInsightsService.analyzeScoutWorkload(
          this.scoutId,
          this.allGameReports,
        );
      }
    },

    togglePanel() {
      this.isExpanded = !this.isExpanded;
    },

    toggleSettings() {
      this.showSettings = !this.showSettings;
    },

    updateSettings() {
      this.$emit("settings-changed", this.settings);
      // Re-analyze with new settings
      this.analyzeCurrentPlayer();
    },

    reviewDuplicate(duplicate) {
      this.$emit("duplicate-reviewed", duplicate);
    },

    dismissDuplicate(duplicate) {
      this.duplicateAlerts = this.duplicateAlerts.filter(
        (d) => d !== duplicate,
      );
    },

    completeAction(action) {
      this.$emit("action-selected", {
        type: "complete",
        action: action,
      });
    },

    scheduleAction(action) {
      this.$emit("action-selected", {
        type: "schedule",
        action: action,
      });
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString("en-GB");
    },

    getConfidenceClass(confidenceLevel) {
      const level = confidenceLevel.toLowerCase();
      return `confidence-${level.replace(" ", "-")}`;
    },

    getCategoryClass(categoryType) {
      const type = categoryType.toLowerCase().replace(/\s+/g, "-");
      return `category-${type}`;
    },

    getPriorityClass(priority) {
      return `priority-${priority.toLowerCase()}`;
    },

    getActionIcon(iconName) {
      const iconMap = {
        phone: "bi-telephone-fill",
        calendar: "bi-calendar-event",
        eye: "bi-eye-fill",
        "trending-up": "bi-trending-up",
        clock: "bi-clock-history",
        archive: "bi-archive-fill",
        star: "bi-star-fill",
        users: "bi-people-fill",
        map: "bi-map-fill",
        shield: "bi-shield-check",
        target: "bi-bullseye",
      };
      return iconMap[iconName] || "bi-circle-fill";
    },
  },
};
</script>

<style lang="scss" scoped>
.ai-insights-panel {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-bottom: 1.5rem;
  overflow: hidden;

  .insights-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.5rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;

    .header-content {
      h3 {
        margin: 0;
        font-size: 1.1rem;
        font-weight: 600;
        display: flex;
        align-items: center;
        gap: 0.5rem;

        i {
          color: #ffd700;
        }
      }

      p {
        margin: 0.25rem 0 0 0;
        font-size: 0.85rem;
        opacity: 0.9;
      }
    }

    .toggle-btn {
      background: rgba(255, 255, 255, 0.2);
      border: none;
      border-radius: 6px;
      color: white;
      padding: 0.5rem;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover,
      &.active {
        background: rgba(255, 255, 255, 0.3);
      }
    }
  }

  .insights-content {
    padding: 1.5rem;
  }

  .insight-section {
    margin-bottom: 2rem;

    &:last-child {
      margin-bottom: 0;
    }

    .section-header {
      margin-bottom: 1rem;

      h4 {
        margin: 0;
        font-size: 1rem;
        font-weight: 600;
        color: #2c3e50;
        display: flex;
        align-items: center;
        gap: 0.5rem;

        i {
          color: #667eea;
        }
      }
    }
  }

  // Duplicate Alerts
  .duplicate-alerts {
    .duplicate-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 1rem;
      border-radius: 8px;
      margin-bottom: 0.75rem;
      border-left: 4px solid;

      &.confidence-very-high {
        background: #fff5f5;
        border-color: #e53e3e;
      }

      &.confidence-high {
        background: #fffbf0;
        border-color: #dd6b20;
      }

      &.confidence-medium {
        background: #f7fafc;
        border-color: #4299e1;
      }

      .duplicate-info {
        flex: 1;

        .player-match {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 0.25rem;

          .similarity {
            background: #e2e8f0;
            color: #4a5568;
            padding: 0.125rem 0.5rem;
            border-radius: 12px;
            font-size: 0.75rem;
            font-weight: 600;
          }
        }

        .match-details {
          color: #6c757d;
          font-size: 0.85rem;
          margin-bottom: 0.25rem;
        }

        .confidence-badge {
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          opacity: 0.8;
        }
      }

      .duplicate-actions {
        display: flex;
        gap: 0.5rem;

        button {
          padding: 0.375rem 0.75rem;
          border-radius: 6px;
          border: none;
          font-size: 0.8rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;

          &.btn-review {
            background: #667eea;
            color: white;

            &:hover {
              background: #5a67d8;
            }
          }

          &.btn-dismiss {
            background: #e2e8f0;
            color: #4a5568;

            &:hover {
              background: #cbd5e0;
            }
          }
        }
      }
    }
  }

  // Player Category
  .player-category {
    .category-content {
      .primary-category {
        display: flex;
        align-items: center;
        gap: 1rem;
        margin-bottom: 1rem;

        .category-badge {
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-weight: 600;
          font-size: 0.9rem;

          &.category-elite-prospect {
            background: linear-gradient(135deg, #ffd700, #ff8c00);
            color: white;
          }

          &.category-technical-leader {
            background: linear-gradient(135deg, #4299e1, #3182ce);
            color: white;
          }

          &.category-physical-powerhouse {
            background: linear-gradient(135deg, #e53e3e, #c53030);
            color: white;
          }

          &.category-mental-strategist {
            background: linear-gradient(135deg, #38a169, #2f855a);
            color: white;
          }

          &.category-skillful-technician {
            background: linear-gradient(135deg, #805ad5, #6b46c1);
            color: white;
          }

          &.category-raw-talent {
            background: linear-gradient(135deg, #ed8936, #dd6b20);
            color: white;
          }

          &.category-solid-contributor {
            background: linear-gradient(135deg, #4fd1c7, #38b2ac);
            color: white;
          }

          &.category-development-project {
            background: linear-gradient(135deg, #fbb6ce, #ed64a6);
            color: white;
          }

          &.category-limited-potential {
            background: linear-gradient(135deg, #a0aec0, #718096);
            color: white;
          }
        }

        .confidence-meter {
          flex: 1;
          max-width: 200px;

          .confidence-bar {
            height: 6px;
            background: #e2e8f0;
            border-radius: 3px;
            overflow: hidden;
            margin-bottom: 0.25rem;

            .confidence-fill {
              height: 100%;
              background: linear-gradient(90deg, #667eea, #764ba2);
              border-radius: 3px;
              transition: width 0.3s ease;
            }
          }

          .confidence-text {
            font-size: 0.75rem;
            color: #6c757d;
            font-weight: 500;
          }
        }
      }

      .category-description {
        color: #4a5568;
        margin-bottom: 1rem;
        line-height: 1.5;
      }

      .strengths-weaknesses {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 1rem;
        margin-bottom: 1rem;

        @media (max-width: 768px) {
          grid-template-columns: 1fr;
        }

        h5 {
          margin: 0 0 0.5rem 0;
          font-size: 0.85rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .attribute-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 0.375rem;

          .attribute-tag {
            padding: 0.25rem 0.75rem;
            border-radius: 12px;
            font-size: 0.75rem;
            font-weight: 500;

            &.strength {
              background: #c6f6d5;
              color: #22543d;
            }

            &.weakness {
              background: #fed7d7;
              color: #742a2a;
            }
          }
        }
      }

      .potential-role {
        h5 {
          margin: 0 0 0.5rem 0;
          font-size: 0.85rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .role-badge {
          background: #edf2f7;
          color: #2d3748;
          padding: 0.375rem 0.75rem;
          border-radius: 6px;
          font-weight: 500;
        }
      }
    }
  }

  // Follow-up Actions
  .follow-up-actions {
    .actions-list {
      .action-item {
        display: flex;
        align-items: flex-start;
        gap: 1rem;
        padding: 1rem;
        border-radius: 8px;
        margin-bottom: 0.75rem;
        border-left: 4px solid;
        background: #f8f9fa;

        &.priority-high {
          border-color: #e53e3e;
          background: #fff5f5;
        }

        &.priority-medium {
          border-color: #dd6b20;
          background: #fffbf0;
        }

        &.priority-low {
          border-color: #4299e1;
          background: #f7fafc;
        }

        .action-icon {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #667eea;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .action-content {
          flex: 1;

          .action-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 0.5rem;

            h5 {
              margin: 0;
              font-size: 0.9rem;
              font-weight: 600;
              color: #2c3e50;
            }

            .priority-badge {
              padding: 0.125rem 0.5rem;
              border-radius: 12px;
              font-size: 0.7rem;
              font-weight: 600;
              text-transform: uppercase;

              &.priority-high {
                background: #fed7d7;
                color: #742a2a;
              }

              &.priority-medium {
                background: #feebc8;
                color: #7b341e;
              }

              &.priority-low {
                background: #bee3f8;
                color: #2a4365;
              }
            }
          }

          .action-description {
            margin: 0 0 0.75rem 0;
            color: #4a5568;
            line-height: 1.4;
          }

          .action-meta {
            display: flex;
            gap: 1rem;
            font-size: 0.8rem;
            color: #6c757d;

            span {
              display: flex;
              align-items: center;
              gap: 0.25rem;
            }
          }
        }

        .action-controls {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;

          button {
            width: 32px;
            height: 32px;
            border-radius: 6px;
            border: none;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.2s ease;

            &.btn-complete {
              background: #48bb78;
              color: white;

              &:hover {
                background: #38a169;
              }
            }

            &.btn-schedule {
              background: #4299e1;
              color: white;

              &:hover {
                background: #3182ce;
              }
            }
          }
        }
      }
    }
  }

  // Scout Workload
  .scout-workload {
    .workload-content {
      .workload-metrics {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 1rem;
        margin-bottom: 1rem;

        .metric-item {
          text-align: center;
          padding: 1rem;
          background: #f8f9fa;
          border-radius: 8px;

          .metric-value {
            display: block;
            font-size: 1.5rem;
            font-weight: 700;
            color: #667eea;
            margin-bottom: 0.25rem;
          }

          .metric-label {
            font-size: 0.8rem;
            color: #6c757d;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
        }
      }

      .workload-recommendations {
        h5 {
          margin: 0 0 0.5rem 0;
          font-size: 0.9rem;
          font-weight: 600;
          color: #2c3e50;
        }

        ul {
          margin: 0;
          padding-left: 1.25rem;

          li {
            margin-bottom: 0.25rem;
            color: #4a5568;
            line-height: 1.4;
          }
        }
      }
    }
  }

  // Settings
  .insights-settings {
    border-top: 1px solid #e2e8f0;
    padding-top: 1rem;

    .settings-toggle {
      background: none;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      padding: 0.5rem 1rem;
      color: #4a5568;
      cursor: pointer;
      font-size: 0.85rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      transition: all 0.2s ease;

      &:hover {
        background: #f8f9fa;
        border-color: #cbd5e0;
      }
    }

    .settings-panel {
      margin-top: 1rem;
      padding: 1rem;
      background: #f8f9fa;
      border-radius: 8px;

      .setting-item {
        margin-bottom: 0.75rem;

        &:last-child {
          margin-bottom: 0;
        }

        .setting-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.85rem;
          color: #4a5568;
          cursor: pointer;

          input[type="checkbox"] {
            margin: 0;
          }

          input[type="range"] {
            flex: 1;
            margin: 0 0.5rem;
          }
        }
      }
    }
  }
}
</style>
